import moment from 'moment-timezone';

import type { IService, TFlags, TFlagsRecord } from '@feathr/blackbox';
import { CampaignClass, EServiceItemIds } from '@feathr/blackbox';

import type { TLicenseFeatures } from './useLicense.types';

type TExceptionHandler = (props: IExceptionProps) => TLicenseFeatures;

interface IExceptionProps {
  activeServices: IService[];
  license: TLicenseFeatures;
  flags: TFlags[];
}

interface ILicenseExceptionHandler {
  /** Condition that determines if the exception handler should be applied */
  applies: ({
    services,
    flags,
  }: {
    services: IService[];
    flags?: Array<keyof TFlagsRecord>;
  }) => boolean;
  /** Function to apply exception to the license */
  handle: TExceptionHandler;
  /** Description of what this exception handler does */
  description: string;
}

/**
 * Accounts with the Platform Implementation and Onboarding service
 * will have the Implementations page visible for the first 90 days
 * of their service. Otherwise, visibility is based on the package.
 */
const handlePlatformImplementationAndOnboarding: TExceptionHandler = ({
  activeServices,
  license,
}) => {
  const activePlatformImplementationAndOnboardingService = activeServices.find(
    (s) => s.item_id === EServiceItemIds.PlatformImplementationAndOnboarding,
  );

  if (activePlatformImplementationAndOnboardingService) {
    const { start } = activePlatformImplementationAndOnboardingService.period;
    const isWithin90Days = moment(start).add(90, 'days').isAfter(moment());
    if (isWithin90Days) {
      return {
        ...license,
        implementationsPageVisible: true,
      };
    }
  }

  return license;
};

/**
 * Until now, accounts had unrestricted access to the Implementations page.
 * This flag allows us to override the package-based access control for customers
 * who have been using the feature and have an active package that does not
 * include access.
 */
const handleOverridePackageImplementationsAccess: TExceptionHandler = ({ license, flags }) => {
  if (flags.includes('overridePackageImplementationsAccess')) {
    return {
      ...license,
      implementationsPageVisible: true,
    };
  }
  return license;
};

/**
 * This flag allows us to override the package-based access control for Drip campaigns.
 */
const handleOverrideDripCampagns: TExceptionHandler = ({ license, flags }) => {
  if (flags.includes('showDripCampaigns')) {
    // https://github.com/Feathr/shrike/wiki/Account-Licenses#drip-campaigns
    return {
      ...license,
      [CampaignClass.Drip]: true,
      drip: true,
    };
  }
  return license;
};

/**
 * Accounts with any active service will have the Implementations page visible
 */
const handleActiveService: TExceptionHandler = ({ license }) => {
  return {
    ...license,
    implementationsPageVisible: true,
  };
};

// Add new exception handlers here 🙏
const exceptionHandlers: ILicenseExceptionHandler[] = [
  {
    applies: ({ services }) =>
      services.some((s) => s.item_id === EServiceItemIds.PlatformImplementationAndOnboarding),
    handle: handlePlatformImplementationAndOnboarding,
    description:
      'Enables Implementations page for accounts with Platform Implementation service for first 90 days',
  },
  {
    applies: ({ services }) => services.length > 0,
    handle: handleActiveService,
    description: 'Enables Implementations page for accounts with any active service',
  },
  {
    applies: ({ flags }) => flags?.includes('overridePackageImplementationsAccess') ?? false,
    handle: handleOverridePackageImplementationsAccess,
    description:
      'Enables Implementations page for accounts with the overridePackageImplementationsAccess flag',
  },
  {
    applies: ({ flags }) => flags?.includes('showDripCampaigns') ?? false,
    handle: handleOverrideDripCampagns,
    description: 'Enables Drip campaigns for accounts with the showDripCampaigns flag',
  },
];

export function applyExceptions({
  activeServices,
  license,
  flags,
}: IExceptionProps): TLicenseFeatures {
  return exceptionHandlers.reduce(
    (currentLicense, { applies, handle }) => {
      if (applies({ services: activeServices }) || flags.length > 0) {
        return handle({ activeServices, license: currentLicense, flags });
      }
      return currentLicense;
    },
    { ...license },
  );
}
